import { Back, TimelineLite, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";

import {Application} from "../Application";
import Gameplay from "../Gameplay";

import * as Constants from "../Data/Constants";
import { Culture } from "../Data/Culture";
import * as SFX from "../Data/SoundEffect";
import * as Topics from "../Data/Topics";
import * as VO from "../Data/VoiceOver";

import { Balloon } from "./Balloon";
import { ConfettiRain } from "./ConfettiRain";
import SpotlightParticles from "./SpotlightParticles";
import TimedButton from "./TimedButton";

const IDLE_TIMEOUT = 10000;

export default class EffectsButtons extends PIXI.Container {
    protected readonly _spotlightsButton  = this.makeFXButton("fx-button-lights", 2000, 3000, VO.U10);
    protected readonly _balloonsButton    = this.makeFXButton("fx-button-balloons", 4000, 2000, VO.U9);
    protected readonly _confettiButton    = this.makeFXButton("fx-button-confetti", 3000, 2000, VO.U8);

    protected readonly _spotlightParticles = this.addChild(new SpotlightParticles([
        {color: 0xFFE850, size: "small"},
        {color: 0xFFA3EF, size: "small"},
        {color: 0xA0EAFF, size: "small"},
        {color: 0x6CDC22, size: "big"},
    ]));
    protected readonly _confetti           = this.addChild(new ConfettiRain());

    protected _size: PIXI.Point = new PIXI.Point();
    protected _messageTokens: any[];
    protected _hintTimeline?: TimelineLite;
    private _spotlightsTimeout?: number;
    private _buttonTween?: gsap.Animation;

    public get buttons() { return [this._spotlightsButton, this._confettiButton, this._balloonsButton]; }

    constructor() {
        super();
        this._messageTokens = new Array<any>();
        this._messageTokens.push(
            PubSub.subscribe(Topics.FX_BUTTON_CLICKED, this.onFxButtonClicked.bind(this)),
            PubSub.subscribe(Topics.POWERUP_ONE_UNLOCKED, this.onPowerupUnlocked.bind(this)),
            PubSub.subscribe(Topics.POWERUP_TWO_UNLOCKED, this.onPowerupUnlocked.bind(this)),
            PubSub.subscribe(Topics.POWERUP_THREE_UNLOCKED, this.onPowerupUnlocked.bind(this)),
        );

        this._spotlightsButton.visible = false;
        this._confettiButton.visible = false;
        this._balloonsButton.visible = false;

        this._spotlightsButton.locked = true;
        this._confettiButton.locked = true;
        this._balloonsButton.locked = true;
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._messageTokens.forEach(PubSub.unsubscribe);
        clearTimeout(this._spotlightsTimeout);
        this._hintTimeline?.kill();
        super.destroy(pOptions);
    }

    public onResize(pSize: PIXI.Point) {
        this._size = pSize;
        const left = -(pSize.x / 2) + 10;
        const top = -(pSize.y / 2) + 60 + 10;

        // if a button is animating in, skip to the end of its animation before repositioning
        if (this._buttonTween?.isActive()) {
            this._buttonTween.progress(1, false);
        }

        // position spotlights button below bottom of closed captions
        this._spotlightsButton.position.set(
            left + this._spotlightsButton.width / 2,
            top + this._spotlightsButton.height / 2,
        );
        // position confetti button below spotlights button
        this._confettiButton.position.set(
            left + this._confettiButton.width / 2,
            this._spotlightsButton.position.y + this._confettiButton.height,
        );
        // position balloons button below confetti button
        this._balloonsButton.position.set(
            left + this._balloonsButton.width / 2,
            this._confettiButton.position.y + this._confettiButton.height,
        );

        // position confetti spawn point top center of screen
        this._confetti.position.set(0, -pSize.y / 2);
    }

    protected onPowerupUnlocked(_pTopic: string, pData: number) {
        this.buttons[pData].locked = false;
        this.buttons[pData].visible = true;
        Application.instance.audio.play(SFX.BUTTON_APPEAR, 1, false, HLF.AudioCategory.SFX);
        this._buttonTween = TweenLite.from(this.buttons[pData], 1, {
            pixi: {
                positionX: -this._size.x / 2, // fly in from offscreen left
                scaleX: 0,
                scaleY: 0,
            },
            ease: Back.easeOut.config(1.6),
        }).eventCallback(Constants.TWEEN_ONCOMPLETE, () => {
            PubSub.publishSync(HLF.REGISTER_FOCUSABLE, this.buttons[pData]);
            // PubSub.publishSync(Topics.KEYBOARD_FOCUSABLE_INTERACTIVE_CHANGED, this.buttons[pData]);
            const firstTimeHintTween = TweenMax.to(this.buttons[pData], 0.5, {pixi: {
                scaleX: 1.1,
                scaleY: 1.1,
            }}).repeat(-1).yoyo(true);
            this.buttons[pData].once(HLF.Input.Events.POINTER_TAP, () => firstTimeHintTween.pause(0).kill());
        });
    }

    protected onFxButtonClicked(_pTopic: typeof Topics.FX_BUTTON_CLICKED, pData: TimedButton) {
        switch (pData) {
            case this._spotlightsButton:
                this.onSpotlightsButtonClicked();
                break;
            case this._balloonsButton:
                this.onBalloonsButtonClicked();
                break;
            case this._confettiButton:
                this.onConfettiButtonClicked();
                break;
        }
    }

    protected makeFXButton(
        pForegroundImage: string, pDuration: number = 1000, pCooldown: number = 3000, pHoverVO: string,
    ) {
        const button: TimedButton = new TimedButton(
            () => PubSub.publish(Topics.FX_BUTTON_CLICKED, button),
            "fx-button-bg",
            Constants.SPRITESHEET_UI,
            pDuration,
            pCooldown,
            pHoverVO,
        );
        button.name = pForegroundImage;
        button.setAndAddIcon(pForegroundImage, Constants.SPRITESHEET_UI);
        button.getOnboardingRotation = () => PIXI.DEG_TO_RAD * 90;
        button.getOnboardingPosition = () => {
            return button.toGlobal(new PIXI.Point(button.width / 2, 0));
        };
        button.locked = true;
        this.addChild(button);
        return button;
    }

    private onConfettiButtonClicked() {
        Application.instance.audio.play(SFX.CONFETTI_SPAWN, 1, false, HLF.AudioCategory.SFX);
        this._confetti.play();
    }

    private onBalloonsButtonClicked() {
        Application.instance.audio.play(SFX.BALLOON_SPAWN, 1, false, HLF.AudioCategory.SFX);
        switch (Gameplay.Instance.currentCulture) {
            case Culture.Egypt:
                this.parent.addChild(...Balloon.Egypt());
                this.parent.addChild(...Balloon.Egypt());
                this.parent.addChild(...Balloon.Egypt());
                break;
            case Culture.India:
                this.parent.addChild(...Balloon.India());
                this.parent.addChild(...Balloon.India());
                this.parent.addChild(...Balloon.India());
                break;
            case Culture.Mexico:
                this.parent.addChild(...Balloon.Mexico());
                this.parent.addChild(...Balloon.Mexico());
                this.parent.addChild(...Balloon.Mexico());
                break;
        }
    }

    private onSpotlightsButtonClicked() {
        this._spotlightParticles.FadeIn();
        this._spotlightsTimeout = setTimeout(() => this._spotlightParticles.FadeOut(), 3000);
    }

}
